/**
 *
 * OfferAddOns
 *
 */

import React from "react";
import PropTypes from "prop-types";
import { IntlPropType, GetCountryCode } from "root/libs/core-libs/src";
import { selectTrackingKey } from "one-time-shipment/src/containers/Tracking/selectors";
import { showCurrencyAsCode, offerFeatures } from "root/libs/constants/src";
import {
  isEnter,
  isSpaceBar,
} from "one-time-shipment/src/helpers/spaceBarEvents";
import {
  AddOnsWrapper,
  AddOnsContent,
  OfferAddOnsContentExtraStyles,
  StyledHeadline,
  StyledAccordion,
  StyledPanel,
  StyledContent,
} from "./styled";

const COPY_ID_PREFIX = "OfferAddOns";
const ADD_ON_MSG_COPY_ID_PREFIX = "AddOnsMessages";

export default class OfferAddOns extends React.PureComponent {
  static propTypes = {
    /** These are the addOns that can be added to an offer */
    addOns: PropTypes.array.isRequired,
    /** will combined with the add on KEY to create a unique ID for the accordion */
    offerKey: PropTypes.string,
    /** Translations library  */
    intl: IntlPropType.isRequired,
    trackEvent: PropTypes.func.isRequired,
    showCurrencyIsoCodeForPrices: PropTypes.bool,
    nativeLanguageCode: PropTypes.string,
    formatCurrencyWithNativeLocale: PropTypes.bool,
    customHeadline: PropTypes.string,
    variationType: PropTypes.string,
    isOverAllAccordianOpen: PropTypes.bool,
  };

  static defaultProps = {
    offerKey: "",
    showCurrencyIsoCodeForPrices: false,
    nativeLanguageCode: "",
    formatCurrencyWithNativeLocale: false,
    isOverAllAccordianOpen: true, // Default to true when not provided
  };

  constructor(props) {
    super(props);
    this.trackingKeySelector = selectTrackingKey;
    this.addOnExploredKeyPath = this.trackingKeySelector(
      "spotShipment.interactions.offer.idExploredAddOn"
    );

    this.state = {
      activeAccordianIndex: -1,
    };
    this.firstAccordionTriggerRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { isOverAllAccordianOpen } = this.props;
    // Only focus the add-ons accordion when the overall accordion is open
    // Check if isOverAllAccordianOpen is defined (might not be provided by parent)
    if (
      (prevProps.isOverAllAccordianOpen === false ||
        prevProps.isOverAllAccordianOpen === undefined) &&
      isOverAllAccordianOpen === true &&
      this.firstAccordionTriggerRef.current
    ) {
      this.firstAccordionTriggerRef.current.focus();
    }
  }

  getNameString = (addOn) => {
    let nameSufix = "";
    const {
      intl,
      showCurrencyIsoCodeForPrices,
      nativeLanguageCode,
      formatCurrencyWithNativeLocale,
    } = this.props;

    const countryCode = GetCountryCode(intl.locale);
    const priceFormatLocale = formatCurrencyWithNativeLocale
      ? `${nativeLanguageCode}-${countryCode}`
      : intl.locale;

    if (addOn.price) {
      const price =
        showCurrencyIsoCodeForPrices || addOn.price.currencyCode === ""
          ? `${addOn.price.currencyCode} ${addOn.price.amount}`
          : new Intl.NumberFormat(priceFormatLocale, {
              style: "currency",
              currency: addOn.price.currencyCode,
              currencyDisplay: showCurrencyAsCode.includes(
                addOn.price.currencyCode
              )
                ? "code"
                : "symbol",
            }).format(addOn.price.amount);
      nameSufix = `[+ ${price}]`;
    }

    return nameSufix;
  };

  getAddOnTranslation(addOn) {
    const { intl, offerKey } = this.props;
    const name = intl.formatMessage({
      id: `${ADD_ON_MSG_COPY_ID_PREFIX}.${addOn.key}_name`,
    });

    let descriptionKey = `${ADD_ON_MSG_COPY_ID_PREFIX}.${addOn.key}_description`;
    // Check for specific offerKey conditions
    if (addOn.key === offerFeatures.GO_GREEN) {
      if (offerKey.startsWith("DGF_AFR")) {
        descriptionKey = `${ADD_ON_MSG_COPY_ID_PREFIX}.${addOn.key}_AFR_description`;
      } else if (offerKey.startsWith("DGF_OFR")) {
        descriptionKey = `${ADD_ON_MSG_COPY_ID_PREFIX}.${addOn.key}_OFR_description`;
      }
    }

    const description = intl.formatMessage(
      {
        id: descriptionKey,
      },
      {
        br: <br key="key" />,
        b: (chunks) => <strong>{chunks}</strong>,
        p: (chunks) => <p>{chunks}</p>,
      }
    );

    return {
      name,
      description,
    };
  }

  trackEvent = (addOnProps) => {
    const { trackEvent } = this.props;

    trackEvent({ idExploredAddOn: addOnProps.id });
  };

  getTabIndex = (isOverAllAccordianOpen, idx) => {
    // Default to accessible (true) if isOverAllAccordianOpen is undefined
    const isAccordionOpen = isOverAllAccordianOpen !== false;

    if (!isAccordionOpen) {
      return -1;
    }

    return idx === 0 ? 0 : -1;
  };

  render() {
    const {
      addOns,
      intl,
      offerKey,
      customHeadline,
      variationType,
      isOverAllAccordianOpen,
    } = this.props;

    const { activeAccordianIndex } = this.state;
    const hasAddOns = !!addOns.length;

    let headlineString;

    if (customHeadline) {
      headlineString = intl.formatMessage({ id: customHeadline });
    } else if (hasAddOns) {
      headlineString = intl.formatMessage({ id: `${COPY_ID_PREFIX}.headline` });
    } else {
      headlineString = intl.formatMessage({ id: `${COPY_ID_PREFIX}.noAddOns` });
    }

    // Handle case when isOverAllAccordianOpen is undefined (default to visible/true)
    const isAccordionVisible = isOverAllAccordianOpen !== false;

    return !hasAddOns ? null : (
      <AddOnsWrapper
        variationType={variationType}
        aria-hidden={!isAccordionVisible}
      >
        <AddOnsContent variationType={variationType}>
          <StyledHeadline priority={5} weight="700">
            {headlineString}
          </StyledHeadline>
          <StyledAccordion soloExpandable aria-hidden={!isAccordionVisible}>
            {addOns.map((addOn, idx) => (
              <StyledPanel
                expanded={activeAccordianIndex === idx}
                key={addOn.key}
                id={`${addOn.key}-${offerKey}`}
                ariaControls={`${addOn.key}-${offerKey}-content`}
                headingTag="p"
                ref={idx === 0 ? this.firstAccordionTriggerRef : undefined}
                tabIndex={this.getTabIndex(isOverAllAccordianOpen, idx)}
                onKeyDown={(e) => {
                  if (isEnter(e) || isSpaceBar(e)) {
                    if (activeAccordianIndex === idx) {
                      this.setState({
                        activeAccordianIndex: -1,
                      });
                    } else {
                      this.setState({
                        activeAccordianIndex: idx,
                      });
                    }
                  }
                }}
              >
                <span slot="heading">
                  {this.getAddOnTranslation(addOn).name}{" "}
                  {this.getNameString(addOn)}
                </span>
                <StyledContent
                  dataId={`${addOn.key}-${offerKey}-content`}
                  dataAriaLabelledby={`${addOn.key}-${offerKey}`}
                  dataClassName={OfferAddOnsContentExtraStyles}
                >
                  {this.getAddOnTranslation(addOn).description}
                </StyledContent>
              </StyledPanel>
            ))}
          </StyledAccordion>
        </AddOnsContent>
      </AddOnsWrapper>
    );
  }
}
